import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const BillingContext = createContext(null);

export function BillingProvider({ children }) {
  const { refreshUser } = useAuth();

  // ── Core billing data ──
  const [currentPlan, setCurrentPlan] = useState('free');
  const [periodEnd, setPeriodEnd] = useState(null);
  const [isPlanExpired, setIsPlanExpired] = useState(false);
  const [plans, setPlans] = useState({});
  const [quota, setQuota] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [pendingPlan, setPendingPlan] = useState(null);
  const [userBillingCycle, setUserBillingCycle] = useState('monthly');
  const [userCreatedAt, setUserCreatedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Plan history ──
  const [planHistory, setPlanHistory] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ── Payment flow state ──
  const [upgrading, setUpgrading] = useState(null);
  const [checkoutLocked, setCheckoutLocked] = useState(false);

  // ── Load overview data ──
  const loadOverview = useCallback(async () => {
    try {
      const [plansRes, quotaRes] = await Promise.all([
        api.get('/api/billing/plans'),
        api.get('/api/profile/quota'),
      ]);
      setPlans(plansRes.data.plans || {});
      setCurrentPlan(plansRes.data.current_plan || 'free');
      setSubscriptionStatus(plansRes.data.subscription_status || 'active');
      setPendingPlan(plansRes.data.pending_plan || null);
      setPeriodEnd(plansRes.data.period_end || null);

      // Check if paid plan has expired
      const plan = plansRes.data.current_plan || 'free';
      if (plan !== 'free' && plansRes.data.period_end) {
        const endDate = new Date(plansRes.data.period_end);
        if (Date.now() > endDate.getTime()) {
          setIsPlanExpired(true);
        } else {
          setIsPlanExpired(false);
        }
      }

      setQuota(quotaRes.data);
    } catch (err) {
      console.error('Failed to load billing overview:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load user billing cycle ──
  const fetchUserBillingCycle = useCallback(async () => {
    try {
      const res = await api.get('/api/auth/me');
      setUserBillingCycle(res.data.billing_cycle || 'monthly');
      setUserCreatedAt(res.data.created_at || null);
    } catch (err) {
      console.error('Failed to fetch billing cycle:', err);
    }
  }, []);

  // ── Load history tab data ──
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const [historyRes, receiptsRes, pendingRes] = await Promise.all([
        api.get('/api/billing/history'),
        api.get('/api/billing/receipts'),
        api.get('/api/billing/pending-payments').catch(() => ({ data: { payments: [] } })),
      ]);
      setPlanHistory(historyRes.data.history || []);
      setReceipts(receiptsRes.data.receipts || []);
      setPendingPayments(pendingRes.data.payments || []);
    } catch (err) {
      console.error('Failed to load billing history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // ── Initialize on mount ──
  useEffect(() => {
    // Check for checkout lock
    const inProgress = localStorage.getItem('checkout_in_progress');
    if (inProgress) {
      try {
        const data = JSON.parse(inProgress);
        if (Date.now() - data.timestamp < 15 * 60 * 1000) {
          setCheckoutLocked(true);
          setUpgrading(data.plan);
        } else {
          localStorage.removeItem('checkout_in_progress');
          localStorage.removeItem('checkout_session_id');
        }
      } catch {
        localStorage.removeItem('checkout_in_progress');
        localStorage.removeItem('checkout_session_id');
      }
    }

    // Load all initial data
    Promise.all([loadOverview(), fetchUserBillingCycle()]);
  }, [loadOverview, fetchUserBillingCycle]);

  // ── Full refresh after plan change ──
  const refreshAll = useCallback(async () => {
    await Promise.all([loadOverview(), fetchUserBillingCycle()]);
    await refreshUser().catch(() => {});
  }, [loadOverview, fetchUserBillingCycle, refreshUser]);

  // ── Handle cancellation completion ──
  const handleCancelled = useCallback(async () => {
    await loadOverview();
  }, [loadOverview]);

  // ── Handle payment completion ──
  const handlePaymentComplete = useCallback(async () => {
    await refreshAll();
  }, [refreshAll]);

  // ── Unlock checkout ──
  const unlockCheckout = useCallback(() => {
    localStorage.removeItem('checkout_in_progress');
    localStorage.removeItem('checkout_session_id');
    setCheckoutLocked(false);
    setUpgrading(null);
  }, []);

  const value = {
    // Data
    currentPlan, periodEnd, isPlanExpired, plans, quota,
    subscriptionStatus, pendingPlan, userBillingCycle, userCreatedAt, loading,
    planHistory, receipts, pendingPayments, loadingHistory,
    upgrading, checkoutLocked,

    // Actions
    setCurrentPlan, setSubscriptionStatus, setPendingPlan,
    setUpgrading, setCheckoutLocked, setLoading,
    loadOverview, loadHistory, refreshAll,
    handleCancelled, handlePaymentComplete, unlockCheckout,
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
}
